"use client";

import { c as _c } from "react/compiler-runtime";
import { XMarkIcon } from "@heroicons/react/24/outline";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ReactNode } from "react";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { useDropVoteLogs } from "@/hooks/useDropVoteLogs";
import { useDropVoters } from "@/hooks/useDropVoters";
import { ParticipationDropVoteDetailsLogRow, ParticipationDropVoteDetailsVoterRow } from "./ParticipationDropVoteDetailsRows";
type VoteDetailsTab = "voters" | "logs";
interface ParticipationDropVoteDetailsContentProps {
  readonly drop: ApiDrop;
  readonly activeTab: VoteDetailsTab;
  readonly isOpen: boolean;
  readonly onActiveTabChange: (tab: VoteDetailsTab) => void;
  readonly onClose: () => void;
  readonly showHeader: boolean;
}
interface VoteDetailsErrorStateProps {
  readonly label: string;
  readonly onRetry: () => void;
}
const getVoteValueLabel = (vote: number): string => {
  if (vote === 0) {
    return "0";
  }
  const sign = vote < 0 ? "-" : "";
  return `${sign}${formatNumberWithCommas(Math.abs(vote))}`;
};
function LoadingBar() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-h-0.5 tw-w-full tw-overflow-hidden tw-bg-iron-800"><div className="tw-h-full tw-w-full tw-animate-loading-bar tw-bg-indigo-400" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function VoteDetailsEmptyState(t0) {
  const $ = _c(2);
  const {
    label
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <div className="tw-flex tw-min-h-32 tw-items-center tw-justify-center tw-px-4 tw-py-8"><span className="tw-text-center tw-text-sm tw-font-medium tw-text-iron-500">{label}</span></div>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function VoteDetailsErrorState(t0) {
  const $ = _c(7);
  const {
    label,
    onRetry
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="tw-text-center tw-text-sm tw-font-medium tw-text-rose-300">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onRetry) {
    t2 = <button type="button" onClick={event => {
      event.stopPropagation();
      onRetry();
    }} className="tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-iron-200 tw-transition-colors desktop-hover:hover:tw-border-iron-500 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-white">Try again</button>;
    $[2] = onRetry;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="tw-flex tw-min-h-32 tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-px-4 tw-py-8">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function TabButton(t0) {
  const $ = _c(7);
  const {
    active,
    children,
    onClick
  } = t0;
  let t1;
  if ($[0] !== onClick) {
    t1 = event => {
      event.stopPropagation();
      onClick();
    };
    $[0] = onClick;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `tw-flex-1 tw-rounded-md tw-border-0 tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-transition-colors ${active ? "tw-bg-iron-700 tw-text-white" : "tw-bg-transparent tw-text-iron-400 desktop-hover:hover:tw-bg-white/[0.04] desktop-hover:hover:tw-text-iron-200"}`;
  let t3;
  if ($[2] !== active || $[3] !== children || $[4] !== t1 || $[5] !== t2) {
    t3 = <button type="button" role="tab" aria-selected={active} onClick={t1} className={t2}>{children}</button>;
    $[2] = active;
    $[3] = children;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
export function ParticipationDropVoteDetailsContent(t0) {
  const $ = _c(66);
  const {
    drop,
    activeTab,
    isOpen,
    onActiveTabChange,
    onClose,
    showHeader
  } = t0;
  const creditLabel = WAVE_VOTING_LABELS[drop.wave.voting_credit_type];
  const t1 = isOpen && activeTab === "voters";
  let t2;
  if ($[0] !== drop.id || $[1] !== t1) {
    t2 = {
      dropId: drop.id,
      enabled: t1
    };
    $[0] = drop.id;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const votersQuery = useDropVoters(t2);
  const t3 = isOpen && activeTab === "logs";
  let t4;
  if ($[3] !== drop.id || $[4] !== t3) {
    t4 = {
      dropId: drop.id,
      enabled: t3
    };
    $[3] = drop.id;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const logsQuery = useDropVoteLogs(t4);
  let t5;
  if ($[6] !== activeTab || $[7] !== logsQuery || $[8] !== votersQuery) {
    t5 = () => {
      if (activeTab === "voters" && votersQuery.hasNextPage && !votersQuery.isLoading && !votersQuery.isFetchingNextPage) {
        votersQuery.fetchNextPage();
        return;
      }
      if (activeTab === "logs" && logsQuery.hasNextPage && !logsQuery.isLoading && !logsQuery.isFetchingNextPage) {
        logsQuery.fetchNextPage();
      }
    };
    $[6] = activeTab;
    $[7] = logsQuery;
    $[8] = votersQuery;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const intersectionElementRef = useIntersectionObserver(t5);
  let t6;
  if ($[10] !== drop.wave.voting_credit_type || $[11] !== votersQuery) {
    t6 = () => {
      if (votersQuery.isError) {
        return <VoteDetailsErrorState label="Could not load voters." onRetry={() => {
          votersQuery.refetch();
        }} />;
      }
      if (votersQuery.voters.length === 0 && votersQuery.isLoading) {
        return <VoteDetailsEmptyState label="Loading voters..." />;
      }
      if (votersQuery.voters.length === 0) {
        return <VoteDetailsEmptyState label="No voters yet." />;
      }
      return <div className="tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-iron-800">{votersQuery.voters.map(voter => <ParticipationDropVoteDetailsVoterRow key={voter.voter.id} voter={voter} creditType={drop.wave.voting_credit_type} />)}</div>;
    };
    $[10] = drop.wave.voting_credit_type;
    $[11] = votersQuery;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const renderVoters = t6;
  let t7;
  if ($[13] !== drop.wave.voting_credit_type || $[14] !== logsQuery) {
    t7 = () => {
      if (logsQuery.isError) {
        return <VoteDetailsErrorState label="Could not load vote log." onRetry={() => {
          logsQuery.refetch();
        }} />;
      }
      if (logsQuery.logs.length === 0 && logsQuery.isLoading) {
        return <VoteDetailsEmptyState label="Loading vote log..." />;
      }
      if (logsQuery.logs.length === 0) {
        return <VoteDetailsEmptyState label="No vote changes yet." />;
      }
      return <div className="tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-iron-800">{logsQuery.logs.map(log => <ParticipationDropVoteDetailsLogRow key={log.id} log={log} creditType={drop.wave.voting_credit_type} />)}</div>;
    };
    $[13] = drop.wave.voting_credit_type;
    $[14] = logsQuery;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const renderLogs = t7;
  const isLoadingMore = activeTab === "voters" ? votersQuery.isFetchingNextPage || votersQuery.isLoading : logsQuery.isFetchingNextPage || logsQuery.isLoading;
  let t8;
  if ($[16] !== drop.raters_count || $[17] !== onClose || $[18] !== showHeader) {
    t8 = showHeader && <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-800 tw-px-4 tw-py-3"><div className="tw-min-w-0"><h3 className="tw-mb-0 tw-text-sm tw-font-semibold tw-text-iron-50">Votes</h3><p className="tw-mb-0 tw-mt-0.5 tw-text-xs tw-text-iron-500">{formatNumberWithCommas(drop.raters_count)}{" "}{drop.raters_count === 1 ? "voter" : "voters"}</p></div><button type="button" aria-label="Close votes" onClick={event_0 => {
        event_0.stopPropagation();
        onClose();
      }} className="tw-flex tw-size-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-border-0 tw-bg-transparent tw-p-0 tw-text-iron-400 tw-transition-colors desktop-hover:hover:tw-bg-white/[0.04] desktop-hover:hover:tw-text-white"><XMarkIcon className="tw-size-4" /></button></div>;
    $[16] = drop.raters_count;
    $[17] = onClose;
    $[18] = showHeader;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== drop.raters_count) {
    t9 = formatNumberWithCommas(drop.raters_count);
    $[20] = drop.raters_count;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = drop.raters_count === 1 ? "voter" : "voters";
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = <span>{t9}{" "}{t10}</span>;
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span aria-hidden="true">/</span>;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== drop.rating) {
    t13 = getVoteValueLabel(drop.rating);
    $[26] = drop.rating;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== creditLabel || $[29] !== t13) {
    t14 = <span>{t13} {creditLabel} total</span>;
    $[28] = creditLabel;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t11 || $[32] !== t14) {
    t15 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-text-xs tw-font-medium tw-text-iron-500">{t11}{t12}{t14}</div>;
    $[31] = t11;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const t16 = activeTab === "voters";
  let t17;
  if ($[34] !== onActiveTabChange) {
    t17 = () => onActiveTabChange("voters");
    $[34] = onActiveTabChange;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t16 || $[37] !== t17) {
    t18 = <TabButton active={t16} onClick={t17}>Voters</TabButton>;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  const t19 = activeTab === "logs";
  let t20;
  if ($[39] !== onActiveTabChange) {
    t20 = () => onActiveTabChange("logs");
    $[39] = onActiveTabChange;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t19 || $[42] !== t20) {
    t21 = <TabButton active={t19} onClick={t20}>Vote log</TabButton>;
    $[41] = t19;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t18 || $[45] !== t21) {
    t22 = <div role="tablist" aria-label="Vote details" className="tw-mt-3 tw-flex tw-rounded-lg tw-bg-iron-900 tw-p-1">{t18}{t21}</div>;
    $[44] = t18;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t15 || $[48] !== t22) {
    t23 = <div className="tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-800 tw-px-4 tw-py-3">{t15}{t22}</div>;
    $[47] = t15;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== activeTab || $[51] !== renderLogs || $[52] !== renderVoters) {
    t24 = activeTab === "voters" ? renderVoters() : renderLogs();
    $[50] = activeTab;
    $[51] = renderLogs;
    $[52] = renderVoters;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== isLoadingMore) {
    t25 = isLoadingMore && <LoadingBar />;
    $[54] = isLoadingMore;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== intersectionElementRef) {
    t26 = <div ref={intersectionElementRef} />;
    $[56] = intersectionElementRef;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t24 || $[59] !== t25 || $[60] !== t26) {
    t27 = <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-track-iron-900 tw-scrollbar-thumb-iron-600 desktop-hover:hover:tw-scrollbar-thumb-iron-400">{t24}{t25}{t26}</div>;
    $[58] = t24;
    $[59] = t25;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t23 || $[63] !== t27 || $[64] !== t8) {
    t28 = <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col" onClick={_temp}>{t8}{t23}{t27}</div>;
    $[62] = t23;
    $[63] = t27;
    $[64] = t8;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  return t28;
}
function _temp(event) {
  return event.stopPropagation();
}